import { onMounted, onBeforeUnmount, ref } from 'vue';

// Captures the browser's `beforeinstallprompt` event so the app can offer an
// in-app "Install" action (an installable PWA must call prompt() from a user
// gesture, so the event is stashed until the user clicks our button).
//
// Only Chromium-based browsers fire this event; Safari/iOS install via the
// native share sheet and never fire it — there the banner simply stays hidden,
// which is the correct, non-intrusive behaviour.
//
// Dismissal is persisted PER DEVICE, not per user: the browser re-fires the
// event on every page load, so a session-only dismissal came back at each
// login — and on a desktop, where nobody wants the app installed, that meant
// every time. Per-device is also the right scope for shared workstations: the
// answer to "should this machine run CoraFlow as an app" doesn't change with
// whoever signs in next. Chromium's own address-bar install control is
// untouched, so a device that dismissed the banner can still install later.

// Minimal shape of the (non-standard) BeforeInstallPromptEvent.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_STORAGE_KEY = 'coraflow-install-prompt-dismissed';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_STORAGE_KEY) === 'true';
  } catch {
    // Storage blocked (private mode, hardened browser): show the banner rather
    // than silently suppressing a feature the user never turned off.
    return false;
  }
}

function persistDismissed(): void {
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, 'true');
  } catch {
    // Best-effort — the banner still stays hidden for the rest of this session.
  }
}

export function useInstallPrompt() {
  const canInstall = ref(false);
  let deferredPrompt: BeforeInstallPromptEvent | null = null;

  const onBeforeInstallPrompt = (event: Event) => {
    // Prevent the mini-infobar so we can present our own themed prompt.
    event.preventDefault();
    // Still stashed when dismissed, so any future in-app install entry point
    // has a usable prompt; only the banner honours the dismissal.
    deferredPrompt = event as BeforeInstallPromptEvent;
    if (readDismissed()) return;
    canInstall.value = true;
  };

  const onInstalled = () => {
    // Already installed — never show the prompt again this session. Not
    // persisted: the browser stops firing the event for an installed app, and
    // if it is later uninstalled the banner should be offered again.
    deferredPrompt = null;
    canInstall.value = false;
  };

  const promptInstall = async () => {
    if (!deferredPrompt) return;
    const prompt = deferredPrompt;
    // The event can only be used once; clear it regardless of outcome.
    deferredPrompt = null;
    canInstall.value = false;
    await prompt.prompt();
    await prompt.userChoice;
  };

  const dismiss = () => {
    deferredPrompt = null;
    canInstall.value = false;
    persistDismissed();
  };

  onMounted(() => {
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.removeEventListener('appinstalled', onInstalled);
  });

  return { canInstall, promptInstall, dismiss };
}
